import { Box, Button, Flex, Text, useDisclosure } from "@chakra-ui/react"
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import ReactSelect from "react-select"
import { SelectOption } from "src/components/shared/ReactSelect"
import { InputLabel } from "src/components/ui/InputLabel"
import { useHallBookingListService, useLocationListService } from "src/hooks"
import { IHallBooking } from "../../../domain/entities"
import { DashboardWrapper } from "../../wrappers/DashboardWrapper"
import { HallBookingAddDrawerFormController } from "./HallBookingAddDrawerForm"
import { HallBookingDeleteDialogController } from "./HallBookingDeleteDialogController"
import { HallBookingListController } from "./HallBookingList"
import { HallBookingUpdateDrawerFormController } from "./HallBookingUpdateDrawerForm"
import { HallBookingBillAddDrawerFormController } from "./HallBookingBillAddDrawerForm"
import { HallBookingBillPreviewDialogController } from "./HallBookingBillPreviewDialogController"
import { HallBookingBillUpdateDrawerFormController } from "./HallBookingBillUpdateDrawerForm"
import { HallBookingBillDeleteDialogController } from "./HallBookingBillDeleteDialogController"

export const HallBookingsPage: FC = () => {
	const { hallBookingList, isLoading, fetchHallBookingList } =
		useHallBookingListService()
	const [isHallBookingDeleteDialogOpen, setIsHallBookingDeleteDialogOpen] =
		useState(false)
	const { locationList, fetchLocationList } = useLocationListService()
	const hallBookingAddDrawerDisclosure = useDisclosure()
	const hallBookingUpdateDrawerDisclosure = useDisclosure()
	const hallBookingBillAddDrawerDisclosure = useDisclosure()
	const hallBookingBillUpdateDrawerDisclosure = useDisclosure()
	const hallBookingBillViewDrawerDisclosure = useDisclosure()
	const [isHallBookingBillDeleteDialogOpen, setIsHallBookingBillDeleteDialogOpen] =
		useState(false)
	const [selectedLocation, setSelectedLocation] = useState<SelectOption | null>({
		label: "All",
		value: null,
	})
	const locationOptions = useMemo(() => {
		return [
			{
				label: "All",
				value: null,
			},
			...locationList.map((location) => ({
				label: location.name,
				value: location.id,
			})),
		]
	}, [locationList])

	const fetchHallBookings = useCallback(() => {
		fetchHallBookingList({
			locationId: selectedLocation?.value ?? undefined,
			fetch: {
				contact: true,
				location: true,
				hallBookingBill: true,
			},
		})
	}, [fetchHallBookingList, selectedLocation])

	useEffect(() => {
		fetchHallBookings()
	}, [fetchHallBookings])

	useEffect(() => {
		fetchLocationList()
	}, [fetchLocationList])

	const selectedHallBookingForBillRef = useRef<IHallBooking>()
	const selectedHallBookingForBillUpdateRef = useRef<IHallBooking>()
	const selectedHallBookingForBillDeleteRef = useRef<IHallBooking>()
	const updateHallBookingRef = useRef<IHallBooking>()
	const deleteHallBookingRef = useRef<IHallBooking>()
	const selectedHallBookingForBillViewRef = useRef<IHallBooking>()

	const getPageStyles = () => {
		return `
			@media all {
				.printable { display: none;}
			}
			@media print {
				.printable { display: block;}
				body {
					padding: 0.5cm;
				}
				table {
					border: solid #000 !important;
					border-width: 1px 0 0 1px !important;
				}
				table th, table td {
					border: solid #000 !important;
					border-width: 0 1px 1px 0 !important;
				}
			}
		`
	}

	return (
		<DashboardWrapper>
			<Box padding={2}>
				<Flex justifyContent="space-between" alignItems="center">
					<Text fontSize="2xl" fontWeight="bold">
						Hall Bookings
					</Text>
					<Button
						size="sm"
						onClick={hallBookingAddDrawerDisclosure.onOpen}
						colorScheme="blue"
					>
						Add Hall Booking
					</Button>
				</Flex>
				<Box maxW={"230px"}>
					<InputLabel label="Location" />
					<ReactSelect
						onChange={(newValue) => {
							setSelectedLocation(
								locationOptions.find(
									(el) => el.value === newValue?.value,
								) ?? locationOptions[0],
							)
						}}
						value={selectedLocation}
						options={locationOptions}
						isSearchable={false}
					/>
				</Box>
			</Box>
			<HallBookingListController
				list={hallBookingList}
				isLoading={isLoading}
				onUpdate={(hallBooking: IHallBooking) => {
					updateHallBookingRef.current = hallBooking
					hallBookingUpdateDrawerDisclosure.onOpen()
				}}
				onDelete={(hallBooking: IHallBooking) => {
					deleteHallBookingRef.current = hallBooking
					setIsHallBookingDeleteDialogOpen(true)
				}}
				onAddBill={(hallBooking: IHallBooking) => {
					selectedHallBookingForBillRef.current = hallBooking
					hallBookingBillAddDrawerDisclosure.onOpen()
				}}
				onViewBill={(hallBooking: IHallBooking) => {
					selectedHallBookingForBillViewRef.current = hallBooking
					hallBookingBillViewDrawerDisclosure.onOpen()
				}}
				onEditBill={(hallBooking: IHallBooking) => {
					selectedHallBookingForBillUpdateRef.current = hallBooking
					hallBookingBillUpdateDrawerDisclosure.onOpen()
				}}
				onDeleteBill={(hallBooking: IHallBooking) => {
					selectedHallBookingForBillDeleteRef.current = hallBooking
					setIsHallBookingBillDeleteDialogOpen(true)
				}}
				onAddClick={hallBookingAddDrawerDisclosure.onOpen}
			/>
			{hallBookingAddDrawerDisclosure.isOpen ? (
				<HallBookingAddDrawerFormController
					{...hallBookingAddDrawerDisclosure}
					onSuccess={() => fetchHallBookings()}
				/>
			) : null}
			{hallBookingBillAddDrawerDisclosure.isOpen ? (
				<HallBookingBillAddDrawerFormController
					hallBooking={selectedHallBookingForBillRef.current}
					{...hallBookingBillAddDrawerDisclosure}
					onSuccess={() => fetchHallBookings()}
				/>
			) : null}
			{hallBookingUpdateDrawerDisclosure.isOpen && updateHallBookingRef.current ? (
				<HallBookingUpdateDrawerFormController
					{...hallBookingUpdateDrawerDisclosure}
					onSuccess={() => fetchHallBookings()}
					hallBooking={updateHallBookingRef.current}
				/>
			) : null}
			{deleteHallBookingRef.current && (
				<HallBookingDeleteDialogController
					isOpen={isHallBookingDeleteDialogOpen}
					setIsOpen={setIsHallBookingDeleteDialogOpen}
					hallBooking={deleteHallBookingRef.current}
					onSuccess={() => fetchHallBookings()}
				/>
			)}
			{hallBookingBillUpdateDrawerDisclosure.isOpen &&
				selectedHallBookingForBillUpdateRef.current?.bill && (
					<HallBookingBillUpdateDrawerFormController
						hallBookingBill={selectedHallBookingForBillUpdateRef.current.bill}
						{...hallBookingBillUpdateDrawerDisclosure}
						onSuccess={() => fetchHallBookings()}
					/>
				)}
			{selectedHallBookingForBillDeleteRef.current?.bill && (
				<HallBookingBillDeleteDialogController
					isOpen={isHallBookingBillDeleteDialogOpen}
					setIsOpen={setIsHallBookingBillDeleteDialogOpen}
					hallBookingBill={selectedHallBookingForBillDeleteRef.current.bill}
					onSuccess={() => fetchHallBookings()}
				/>
			)}
			<style>{getPageStyles()}</style>
			{hallBookingBillViewDrawerDisclosure.isOpen &&
				selectedHallBookingForBillViewRef.current && (
					<HallBookingBillPreviewDialogController
						hallBookingBill={selectedHallBookingForBillViewRef.current.bill!}
						hallBooking={selectedHallBookingForBillViewRef.current}
						setIsOpen={hallBookingBillViewDrawerDisclosure.onClose}
						{...hallBookingBillViewDrawerDisclosure}
					/>
				)}
		</DashboardWrapper>
	)
}
